import { useField } from "react-final-form";

import {
  Section,
  Grid,
  ChoiceRow,
  Field2,
  RffMoney,
  RffPercent,
  RffSelect,
  RffChips,
  RffPurposeChips,
  RffDelayedPurchase,
} from "../../sizersShared/sizerUi";
import {
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
  isPurchaseLike,
} from "../../../utils/loans/delayedPurchase";
import {
  selectOptionsLoanPurpose,
  sizerOptionsRtlProductType,
  sizerOptionsTermMonths,
} from "../../sizersShared/sizerSelectOptions";
import useSizerCorrespondents from "../../sizersShared/useSizerCorrespondents";
import LeverageOrAmountField from "../../sizersShared/LeverageOrAmountField";

/**
 * A construction holdback only exists on the two products that carry one — a
 * stabilized bridge has no budget to finance, so asking what share of it is
 * financed invites a number that prices nothing.
 */
const FINANCED_BUDGET_PRODUCTS = ["Fix and Flip", "Ground Up Construction"];

// Ground up construction has its own sizer (GUC Sizer), so this one offers the
// RTL products only.
const RTL_SIZER_PRODUCTS = sizerOptionsRtlProductType.filter(
  (p) => p !== "Ground Up Construction",
);

/**
 * MOVED TO LOAN CREATION — the four deferred fields: exit strategy, broker
 * origination points, interest calculation and dutch interest. They are
 * COMMITTED TERMS, not pricing inputs (rtl-guc-sizer-frontend.md §2.1), so they
 * are asked when the scenario becomes a loan rather than to get a quote.
 *
 * Interest calc and dutch interest do move `requirements.requiredLiquidity`
 * (the day-count denominator differs by ~1.4% between 30/360 and Actual/365),
 * so a scenario now prices at the server-side defaults — 30/360, non-dutch —
 * until those terms are committed at create-deal.
 */
export default function LoanDetailsSection({ first, sizing }) {
  const correspondents = useSizerCorrespondents();
  // Single-field subscriptions rather than useFormState: watching the whole form
  // re-renders this section on every keystroke anywhere, and only these two
  // values decide what is on screen.
  const { input: product } = useField("loanProductType", {
    subscription: { value: true },
  });
  const { input: purpose } = useField("loanPurpose", {
    subscription: { value: true },
  });

  const { input: delayedFlag } = useField("delayedPurchase", {
    subscription: { value: true },
  });

  const showFinancedBudget = FINANCED_BUDGET_PRODUCTS.includes(product.value);
  // Purchase for every purchase rule — a delayed purchase (cash-out + the flag)
  // counts, exactly as the retired "Delayed Purchase" purpose did.
  const isPurchase = isPurchaseLike(purpose.value, delayedFlag.value);
  // `outstandingLoanUpb` is declared "Refinance Only" on the model, and the
  // closing engine already reads it when working out required liquidity.
  const showUpb = Boolean(purpose.value) && !isPurchase;

  // Only on a cash-out refinance (REQ-106) — and required while it shows.
  const showDelayed = canBeDelayedPurchase(purpose.value);
  // The RTL products this sizer prices — Ground Up Construction (GUC Sizer),
  // DSCR (its own sizer), Multifamily Bridge and Other are deliberately not
  // offered here.
  const productField = (span) => (
    <Field2 label="Product type" span={span}>
      <RffChips sm name="loanProductType" options={RTL_SIZER_PRODUCTS} />
    </Field2>
  );
  const purposeField = (span) => (
    <Field2 label="Purpose" span={span}>
      <RffPurposeChips sm name="loanPurpose" options={selectOptionsLoanPurpose} />
    </Field2>
  );
  const termField = (span) => (
    <Field2 label="Term" span={span}>
      <RffChips sm name="termMonths" options={sizerOptionsTermMonths} />
    </Field2>
  );

  return (
    <Section
      first={first}
      title="Loan details"
      subtitle="These choices drive which questions appear below."
    >
      {/* THE THREE HIGH-SIGNAL CHOICES, one per line, as CHIP GROUPS — the same
          control the Max Leverage quick check uses for the same two questions
          (rtl-guc-sizer-frontend.md §2.1 calls for pill selectors on the
          high-signal choice sets).

          Chips rather than a segmented pill because the options are not equal
          widths: a segmented control divides its track evenly, which pads
          "12 mo" out to the width of "Ground Up Construction". Chips size to
          their own label and wrap, so each set reads as a list of real options.

          Each decides what the rest of the form asks: product opens the
          construction fields, purpose swaps the purchase/refi set, term moves
          the rate. A dropdown hides two thirds of that behind a click; laid out
          flat, the whole decision space is on screen and the answer is one
          click, not two. */}
      {/* Product type, purpose and term share ONE row, in that order, split
          4 / 5 / 3: purpose carries the longest labels ("Refinance - No Cash
          Out"), term three short ones. Each chip group wraps within its own
          column on a narrow window, and below md the row stacks.

          `termMonths` feeds the rate block's TERM ADJUSTMENT
          (`rate.termAdjPct`, a PERCENT verbatim) — a 12 vs 24 month scenario
          is a different rate, so it is a priced input, not a label. */}
      {/* Not a cash-out refi: the three choices on the 12-column grid, spaced
          4 / 5 / 3. A cash-out refi adds Delayed purchase? right after Purpose,
          and four fields no longer fit fixed tracks — so the row switches to
          content-width fields that wrap only when the window is too narrow. */}
      {showDelayed ? (
        <ChoiceRow>
          {productField()}
          {purposeField()}
          <Field2 label={DELAYED_PURCHASE_LABEL} required>
            <RffDelayedPurchase sm />
          </Field2>
          {termField()}
        </ChoiceRow>
      ) : (
        <Grid cols={12}>
          {productField(4)}
          {purposeField(5)}
          {termField(3)}
        </Grid>
      )}

      {/* HOW THE LOAN IS SIZED gets its own row — EITHER requested leverage OR
          an initial loan amount (owner 2026-10-19). It is the single biggest
          driver of the answer: every amount on the response derives from it.
          CANONICAL names only (`sizerInitialRequestedLeverage` /
          `baseLoanAmount`); the backend derives the authoritative amounts —
          the single-calc rule. */}
      <LeverageOrAmountField isPurchase={isPurchase} sizing={sizing} sx={{ mt: "26px" }} />

      {(showFinancedBudget || showUpb) && (
        <Grid cols={showFinancedBudget && showUpb ? 2 : 1} sx={{ mt: "26px" }}>
          {showFinancedBudget && (
            <Field2
              label="Financed budget"
              hint="Share of the construction budget the loan finances. Below 100%, required liquidity picks up the UNFINANCED portion of the budget on top of interest coverage, unfinanced reserves and unfinanced closing costs."
            >
              <RffPercent name="financedBudgetPercent" factor={100} />
            </Field2>
          )}
          {showUpb && (
            <Field2
              label="Current loan UPB"
              hint="Optional — cash-out proceeds may count toward the required liquidity coverage."
            >
              <RffMoney name="outstandingLoanUpb" placeholder="0" />
            </Field2>
          )}
        </Grid>
      )}

      {/* Counterparty, full width beneath the rest — it scopes the whole
          scenario rather than describing the loan, and its option labels are
          long enough to truncate in a third of a row. Resolves the
          correspondent's RTL account spread server-side, the same resolution the
          lock uses; shown back as `rate.accountSpread`. */}
      <Grid cols={1} sx={{ mt: "26px" }}>
        <Field2
          label="Correspondent"
          hint="Optional — resolves the account rate spread."
        >
          <RffSelect
            name="correspondentId"
            options={correspondents}
            placeholder={
              correspondents.length
                ? "Eastview base (no correspondent)"
                : "Loading…"
            }
          />
        </Field2>
      </Grid>
    </Section>
  );
}
